import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import Swal from 'sweetalert2';
import { Paciente } from '../../pacientes/pacientes';
import {
  ConfiguracionRecetaPDF,
  DatosRecetaPDF,
  RecetasPdf,
} from '../../../core/services/recetas-pdf.service';
import { MatDialog } from '@angular/material/dialog';
import { VistaPreviaRecetaDialog } from './vista-previa-receta-dialog/vista-previa-receta-dialog';
import { AuthService } from '../../../core/services/auth.service';
import { UsuarioService } from '../../../core/services/usuario.service';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import pdfMake from 'pdfmake/build/pdfmake';
import { firstValueFrom } from 'rxjs';
import { ClinicaService } from '../../../core/services/clinica.service';
import { RedSocial } from '../../../core/interface/interface';

@Component({
  selector: 'app-configuracion-recetas',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatOptionModule,
  ],
  templateUrl: './configuracion-recetas.html',
  styleUrl: './configuracion-recetas.scss',
})
export class ConfiguracionRecetas {
  constructor(
    private recetaPdf: RecetasPdf,
    private authService: AuthService,
    private readonly usuarioService: UsuarioService,
  ) {
    this.cargarConfiguracionActual();
    this.cargarLogoInstitucion();
    this.cargarRedesClinica();
    this.cargarUsuarioActual();
  }

  async cargarRedesClinica(): Promise<void> {
    this.cargandoRedes = true;
    try {
      const clinica = await firstValueFrom(this.clinicaService.obtenerClinica());
      this.redesClinica = clinica.redesSociales ?? [];
    } catch {
      this.redesClinica = [];
    } finally {
      this.cargandoRedes = false;
    }
  }

  cargarUsuarioActual(): void {
    const usuario = this.authService.getUser();

    if (!usuario) return;

    this.datosEjemplo.medico = {
      nombre: usuario.nombre ?? '',
      apellidoPaterno: usuario.apellidoPaterno ?? '',
      apellidoMaterno: usuario.apellidoMaterno ?? '',
    };
  }

  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private clinicaService = inject(ClinicaService);

  redesClinica: RedSocial[] = [];
  cargandoRedes = false;

  private datosEjemplo: DatosRecetaPDF = {
    paciente: {
      nombre: 'Juan',
      apellido: 'Pérez',
      segundoApellido: 'García',
      genero: 'Masculino',
      fechaNacimiento: '1990-05-15',
      expediente: {
        alergias: 'Ninguna conocida',
      },
    } as Paciente,

    signosVitales: {
      peso: 75,
      estatura: 1.75,
      frecuenciaCardiaca: 72,
      frecuenciaRespiratoria: 16,
      saturacionOxigeno: 98,
      temperatura: 36.5,
      presionSistolica: 120,
      presionDiastolica: 80,
      observaciones: 'Paciente alerta, orientado y sin datos de dificultad respiratoria.',
    } as any,

    medico: {
      nombre: 'Eduardo',
      apellidoPaterno: 'Ríos',
      apellidoMaterno: 'Pérez',
    },

    medicamentos: [
      {
        medicamento: 'Paracetamol ',
        dosis: '500 mg',
        administracion: '1 tableta por vía oral',
        indicaciones: 'Cada 8 horas, según necesidad',
        duracion: '5 días',
      },
      {
        medicamento: 'Ibuprofeno ',
        dosis: '400 mg',
        administracion: '1 tableta por vía oral',
        indicaciones: 'Cada 12 horas',
        duracion: '3 días',
      },
      {
        medicamento: 'Loratadina ',
        dosis: '10 mg',
        administracion: '1 tableta por vía oral',
        indicaciones: 'Cada 24 horas',
        duracion: '5 días',
      },
      {
        medicamento: 'Solución salina nasal 0.9 %',
        dosis: '2 aplicaciones en cada fosa nasal',
        administracion: 'topica',
        indicaciones: 'Cada 8 horas',
        duracion: '5 días',
      },
    ],

    receta: {
      diagnostico:
        'Infección respiratoria aguda de probable origen viral, acompañada de congestión nasal, tos seca e irritación de garganta.',

      indicaciones:
        'Mantener hidratación adecuada durante el día.\n' +
        'Guardar reposo relativo y evitar cambios bruscos de temperatura.\n' +
        'Realizar lavados nasales con solución salina cada 8 horas.\n' +
        'Evitar el humo del tabaco, polvo y otros irritantes.\n' +
        'Mantener una alimentación equilibrada y dormir entre 7 y 9 horas.',

      observaciones:
        'Acudir a valoración médica si presenta fiebre persistente por más de 48 horas.\n' +
        'Acudir a urgencias en caso de dificultad para respirar o dolor torácico.\n' +
        'No combinar medicamentos sin indicación médica.\n' +
        'Verificar alergias y contraindicaciones antes de administrar cualquier medicamento.',

      // Formato por línea: "Estudio - indicación"
      estudios: [
        {
          nombre: 'Biometría hemática completa',
          indicaciones: 'En ayunas de 8 horas, solo si persisten los síntomas',
        },
        { nombre: 'Química sanguínea de 6 elementos', indicaciones: 'En ayunas de 8 horas' },
        {
          nombre: 'Radiografía de tórax PA y lateral',
          indicaciones: 'Llevar los resultados a la próxima cita',
        },
        { nombre: 'Examen general de orina', indicaciones: 'Primera orina de la mañana' },
      ],

      proximaCita: '2026-10-28',
      motivoProximaCita:
        'Revisión de resultados de laboratorio y valoración de la evolución del cuadro respiratorio.',
      metas:
        'Resolver los síntomas respiratorios en 5 a 7 días y mantener saturación de oxígeno mayor a 95 %.',
      objetivos:
        'Controlar la tos y la congestión nasal, prevenir complicaciones y reincorporar al paciente a sus actividades habituales.',
    },
  };

  form = this.fb.group({
    nombreClinica: [''],
    direccion: [''],
    telefono: [''],
    cedulaProfesional: [''],
    folio: [''],
    noExpediente: [''],
    colorPrincipal: ['#24575A'],
    colorTexto: ['#333333'],

    mostrarRedes: [true],

    mostrarDireccion: [true],
    mostrarTelefono: [true],
    mostrarCedula: [true],
    mostrarFolio: [true],
    mostrarNoExpediente: [true],
    mostrarFecha: [true],
    mostrarFirma: [true],
    nombreFirma: [''],

    mostrarWhatsapp: [true],
    mostrarFacebook: [true],
    mostrarInstagram: [true],
    mostrarPaginaWeb: [true],
    mostrarNoEmail: [true],
    mostrarLogo: [false],

    mostrarMarcaAgua: [true],
    textoMarcaAgua: [''],
    imagenMarcaAguaBase64: [''],
    colorMarcaAgua: ['#6FB6A6'],
    opacidadMarcaAgua: [0.3],
    anguloMarcaAgua: [70],

    disposicion: ['clasico' as 'clasico' | 'moderno' | 'nueva'],
  });

  logoInstitucion: string | null = null;
  cargandoLogo = false;

  cargarConfiguracionActual(): void {
    const config = this.recetaPdf.obtenerConfiguracion();
    this.form.patchValue(config);
  }

  async cargarLogoInstitucion(): Promise<void> {
    this.cargandoLogo = true;
    this.logoInstitucion = await this.recetaPdf.obtenerLogoActualizado();
    this.cargandoLogo = false;
  }

  guardar(): void {
    const valores = this.form.getRawValue() as Partial<ConfiguracionRecetaPDF>;
    this.recetaPdf.configurar(valores);

    Swal.fire({
      icon: 'success',
      title: 'Configuración guardada',
      text: 'Los cambios se aplicarán en la próxima receta generada.',
      showConfirmButton: false,
      timer: 2000,
    });
  }

  restaurarPredeterminada(): void {
    Swal.fire({
      title: '¿Restaurar valores predeterminados?',
      text: 'Se perderá la personalización actual.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, restaurar',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      customClass: {
        title: 'mi-swal-title',
        htmlContainer: 'mi-swal-text',
      },
    }).then((resultado) => {
      if (!resultado.isConfirmed) return;
      this.recetaPdf.restaurarPredeterminada();
      this.cargarConfiguracionActual();
    });
  }

  async vistaPrevia(): Promise<void> {
    const valores = this.form.getRawValue() as Partial<ConfiguracionRecetaPDF>;
    this.recetaPdf.configurar(valores);

    try {
      const blob = await this.recetaPdf.generarRecetaBlob(this.datosEjemplo);
      const pdfUrl = URL.createObjectURL(blob);

      this.dialog.open(VistaPreviaRecetaDialog, {
        width: '90vw',
        height: '90vh',
        maxWidth: '800px',
        panelClass: 'dialogo-vista-previa',
        data: { pdfUrl },
      });
    } catch (error) {
      console.error('Error al generar la vista previa:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No fue posible generar la vista previa de la receta.',
        showConfirmButton: false,
        timer: 2000,
      });
    }
  }

  // ! ==============================
  // ! PRUEBAS DE NUEVA RECETA
  // ! ==============================

  async vistaPreviaRecetaNueva(): Promise<void> {
    try {
      const blob = await this.recetaPdf.generarRecetaNuevaBlob(this.datosEjemplo);
      const pdfUrl = URL.createObjectURL(blob);

      this.dialog.open(VistaPreviaRecetaDialog, {
        width: '90vw',
        height: '90vh',
        maxWidth: '800px',
        panelClass: 'dialogo-vista-previa',
        data: { pdfUrl },
      });
    } catch (error) {
      console.error('Error al generar la vista previa:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No fue posible generar la vista previa de la receta.',
        showConfirmButton: false,
        timer: 2000,
      });
    }
  }
}
